import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useLandmarkStore } from '../state/landmarks';
import {
  ANTWERP_CATEGORIES,
  ANTWERP_CATEGORY_LABEL,
  type AntwerpLandmarkCategory,
} from '../types/antwerp-landmark';
import {
  CUSTOM_POI_ID_PREFIX,
  makeCustomPOIId,
  type CustomPOI,
} from '../types/custom-poi';
import { fileToResizedDataURL } from '../lib/custom-poi-images';

/**
 * Add / edit modal for a user-created POI. Mounts whenever `editingPOI` is
 * non-null in the store. Esc closes; outside click does NOT close (avoid
 * accidental data loss).
 */
export function CustomPOIForm() {
  const editingPOI = useLandmarkStore((state) => state.editingPOI);
  const closePOIForm = useLandmarkStore((state) => state.closePOIForm);
  const saveCustomPOI = useLandmarkStore((state) => state.saveCustomPOI);
  const deleteCustomPOI = useLandmarkStore((state) => state.deleteCustomPOI);
  const toggleArtistSelectionMember = useLandmarkStore(
    (state) => state.toggleArtistSelectionMember,
  );
  const artistSelection = useLandmarkStore((state) => state.artistSelection);
  const customPOIImages = useLandmarkStore((state) => state.customPOIImages);

  if (editingPOI === null) return null;

  return (
    <FormBody
      key={editingPOI.mode === 'edit' ? editingPOI.poi.id : 'new'}
      mode={editingPOI.mode}
      initialCoordinates={
        editingPOI.mode === 'new' ? editingPOI.coordinates : editingPOI.poi.coordinates
      }
      initialPOI={editingPOI.mode === 'edit' ? editingPOI.poi : null}
      initialInSelection={
        editingPOI.mode === 'edit' && artistSelection.has(editingPOI.poi.id)
      }
      existingImage={
        editingPOI.mode === 'edit' ? (customPOIImages[editingPOI.poi.id] ?? null) : null
      }
      onCancel={closePOIForm}
      onSave={async (poi, imageDataURL, inSelection) => {
        await saveCustomPOI(poi, imageDataURL);
        // Sync artist-selection membership to the checkbox value.
        const isCurrentlyIn = artistSelection.has(poi.id);
        if (inSelection !== isCurrentlyIn) {
          toggleArtistSelectionMember(poi.id);
        }
      }}
      onDelete={
        editingPOI.mode === 'edit' ? () => deleteCustomPOI(editingPOI.poi.id) : null
      }
    />
  );
}

interface FormBodyProps {
  mode: 'new' | 'edit';
  initialCoordinates: [number, number];
  initialPOI: CustomPOI | null;
  initialInSelection: boolean;
  existingImage: string | null;
  onCancel: () => void;
  onSave: (poi: CustomPOI, imageDataURL: string | null, inSelection: boolean) => Promise<void>;
  onDelete: (() => Promise<void>) | null;
}

function FormBody({
  mode,
  initialCoordinates,
  initialPOI,
  initialInSelection,
  existingImage,
  onCancel,
  onSave,
  onDelete,
}: FormBodyProps) {
  const [name, setName] = useState(initialPOI?.name ?? '');
  const [category, setCategory] = useState<AntwerpLandmarkCategory>(
    initialPOI?.category ?? 'monument',
  );
  const [period, setPeriod] = useState(initialPOI?.period ?? '');
  const [oneLiner, setOneLiner] = useState(initialPOI?.oneLiner ?? '');
  const [description, setDescription] = useState(initialPOI?.description ?? '');
  const [artistDirection, setArtistDirection] = useState(initialPOI?.artistDirection ?? '');
  const [inSelection, setInSelection] = useState(initialInSelection);
  const [imageDataURL, setImageDataURL] = useState<string | null>(existingImage);
  const [imageDirty, setImageDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Esc to cancel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Focus the first field on mount.
  useEffect(() => {
    const first = dialogRef.current?.querySelector<HTMLInputElement>('input[name="name"]');
    first?.focus();
  }, []);

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Pick an image file (jpg, png, webp…)');
      return;
    }
    try {
      const dataURL = await fileToResizedDataURL(file);
      setImageDataURL(dataURL);
      setImageDirty(true);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load image');
    }
  };

  const canSave = useMemo(
    () => name.trim().length > 0 && oneLiner.trim().length > 0,
    [name, oneLiner],
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const id = initialPOI?.id ?? makeCustomPOIId();
      const poi: CustomPOI = {
        id,
        name: name.trim(),
        category,
        period: period.trim(),
        coordinates: initialCoordinates,
        oneLiner: oneLiner.trim(),
        description: description.trim(),
        stillStands: initialPOI?.stillStands ?? true,
        isCustom: true,
        createdAt: initialPOI?.createdAt ?? Date.now(),
        ...(artistDirection.trim().length > 0 ? { artistDirection: artistDirection.trim() } : {}),
      };
      const newImage = imageDirty ? imageDataURL : null;
      await onSave(poi, newImage, inSelection);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!window.confirm('Delete this POI? This cannot be undone.')) return;
    setSubmitting(true);
    try {
      await onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="custom-poi-form-title"
      data-testid="custom-poi-form"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 'var(--space-6) var(--space-4)',
        background: 'rgba(0, 0, 0, 0.55)',
        overflowY: 'auto',
      }}
    >
      <div
        ref={dialogRef}
        style={{
          width: 'min(560px, 100%)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-elevated)',
          padding: 'var(--space-5)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
          <h2
            id="custom-poi-form-title"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.4rem',
              fontWeight: 700,
              color: 'var(--text-headline)',
              flex: 1,
            }}
          >
            {mode === 'new' ? 'Add a point of interest' : 'Edit point of interest'}
          </h2>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
            {initialCoordinates[1].toFixed(5)}, {initialCoordinates[0].toFixed(5)}
          </span>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <Field label="Name *">
            <input
              name="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={120}
              style={fieldStyle}
            />
          </Field>

          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Field label="Category" style={{ flex: 1 }}>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as AntwerpLandmarkCategory)}
                style={fieldStyle}
              >
                {ANTWERP_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {ANTWERP_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Period" style={{ flex: 1 }}>
              <input
                type="text"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                placeholder="e.g. 1610-1640, 19th c., medieval"
                maxLength={80}
                style={fieldStyle}
              />
            </Field>
          </div>

          <Field label="One-liner *">
            <input
              type="text"
              value={oneLiner}
              onChange={(e) => setOneLiner(e.target.value)}
              required
              maxLength={200}
              placeholder="One sentence shown in tooltips and the panel header"
              style={fieldStyle}
            />
          </Field>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="A few sentences of context for the panel."
              style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </Field>

          <Field label="Artist brief">
            <textarea
              value={artistDirection}
              onChange={(e) => setArtistDirection(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="What the illustrator should draw: mood, distinctive features, composition notes."
              style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </Field>

          <Field label="Hero image">
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
              {imageDataURL ? (
                <img
                  src={imageDataURL}
                  alt=""
                  style={{
                    width: 120,
                    height: 90,
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 120,
                    height: 90,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    color: 'var(--text-muted)',
                    background: 'var(--surface)',
                    border: '1px dashed var(--border)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  no image
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: 1 }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  style={{ fontSize: '0.85rem' }}
                />
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  Auto-resized to 1600 px. Stored in your browser only.
                </span>
                {imageDataURL ? (
                  <button
                    type="button"
                    onClick={() => {
                      setImageDataURL(null);
                      setImageDirty(true);
                    }}
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: 'var(--space-1)',
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                    }}
                  >
                    Remove image
                  </button>
                ) : null}
              </div>
            </div>
          </Field>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: 'var(--space-2) 0',
            }}
          >
            <input
              type="checkbox"
              checked={inSelection}
              onChange={(e) => setInSelection(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
            />
            Add to artist selection
          </label>

          {error ? (
            <p
              role="alert"
              style={{
                fontSize: '0.85rem',
                color: '#c0392b',
                background: 'rgba(192, 57, 43, 0.08)',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {error}
            </p>
          ) : null}

          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-3)',
              borderTop: '1px solid var(--border)',
              paddingTop: 'var(--space-3)',
            }}
          >
            {onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                data-testid="custom-poi-delete"
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  color: '#c0392b',
                  border: '1px solid #c0392b',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                Delete
              </button>
            ) : null}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              style={{
                padding: '8px 14px',
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave || submitting}
              data-testid="custom-poi-save"
              style={{
                padding: '8px 14px',
                background: canSave ? 'var(--primary)' : 'var(--surface)',
                color: canSave ? '#fffdf2' : 'var(--text-muted)',
                border: '1px solid',
                borderColor: canSave ? 'var(--primary)' : 'var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.85rem',
                fontWeight: 700,
                cursor: canSave && !submitting ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Saving…' : mode === 'new' ? 'Save POI' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const fieldStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
};

function Field({
  label,
  children,
  style,
}: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      <span
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

// Silence unused-import warning until we wire ids in callers.
export const _CUSTOM_POI_ID_PREFIX = CUSTOM_POI_ID_PREFIX;
